import { vertexShader } from './shaders/vertex.glsl.js';
import { fragmentShader } from './shaders/fragment.glsl.js';
import { quantize as gifencQuantize, prequantize as gifencPrequantize } from 'gifenc';

export class ColorGridShader {
    constructor(canvas) {
        this.canvas = canvas;
        this.gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
        if (!this.gl) {
            console.error('WebGL not supported');
            return;
        }
        
        this.image = null;
        this.video = null;
        this.isVideo = false;
        this.animationId = null;
        this.isPaused = false;
        this.gridData = [];
        this.rawColors = [];
        this.groupSizes = []; // Track size of each cell's group
        this.gridCols = 15;
        this.gridRows = 15;
        this.gap = 0;
        this.cornerRadius = 0; // 0..0.5 (cell-relative)
        this.hideSource = 0; // 0/1
        this.sourceLighten = 0; // -1..1 (darken..lighten), affects source rendering only
        this.sourceGrayscale = 0; // 0/1, affects source rendering only
        this.backgroundColor = { r: 0, g: 0, b: 0 }; // 0..1
        this.targetColors = 8;
        this.groupThreshold = 20;
        this.revealSize = 20; // Percentile threshold for reveal mask (0-100)
        this.revealDetails = false; // false: reveal flat/low-variance, true: reveal detailed/high-variance
        this.invertReveal = false; // false: normal mask, true: swap revealed vs blocked areas
        this.gradientMode = 0; // 0/1
        this.lineMode = 0; // 0/1
        this.ditherMode = 0; // 0/1
        this.groupRectMode = false;
        this.prevCentroids = null; // Store centroids for video stability

        // Color extraction mode:
        // - 'lab': Google Arts / Art Palette style (k-means in LAB, palette matching in LAB)
        // - 'rgb-kmeans': k-means in RGB (palette matching in RGB)
        // - 'gifenc': gifenc quantize in RGB (palette matching in RGB)
        // - 'rgb': no k-means; use per-cell sampled RGB (quadtree merges use mean RGB)
        this.extractionMode = 'lab';

        // Most frequent (dominant) grid color, normalized 0..1 (used for Dither effect)
        this.dominantGridColor = { r: 0, g: 0, b: 0 };
        this.secondaryGridColor = { r: 0, g: 0, b: 0 };

        // Dither palette stabilization (prevents dominant/secondary colors from rapidly swapping)
        this._ditherDominantKey = null;
        this._ditherSecondaryKey = null;

        // Reveal smoothing (video/webcam): stabilize the alpha mask by smoothing per-cell variance
        this.revealVarianceLerp = 0.12;
        this._revealVarianceSmoothed = null;

        // Further stabilize reveal: smooth the percentile threshold and add hysteresis per cell
        this.revealThresholdLerp = 0.18;
        this.revealHysteresis = 0.035; // fraction of (max-min) variance range
        this._revealThresholdSmoothed = null;
        this._revealMaskState = null; // 0/1 per cell (pre-invert)

        // Final-stage smoothing: smooth the alpha mask itself (video/webcam only)
        this.revealAlphaLerp = 0.2;
        this._alphaMaskSmoothed = null; // Float32Array of 0..255

        // Scratch buffers for performance
        this._varianceScratch = null;

        // Webcam camera switching
        this._webcamDevices = null;
        this._webcamDeviceId = null;
        
        // Offscreen canvas for color sampling
        this.offCanvas = document.createElement('canvas');
        this.offCtx = this.offCanvas.getContext('2d', { willReadFrequently: true });
        this.offCtx.imageSmoothingEnabled = false;
        
        this.initShaders();
    }

    applyGroupRectMode(colors, cols, rows) {
        // Convert connected regions of equal colors into filled bounding-box rectangles.
        // Later discovered groups overwrite earlier ones (draw order / z-order).
        const total = cols * rows;
        if (!colors || colors.length !== total) return;

        const pack = (c) => ((c.r & 255) << 16) | ((c.g & 255) << 8) | (c.b & 255);

        // Cache LAB per unique RGB so we can reassign labs cheaply.
        const labByKey = new Map();
        for (let i = 0; i < total; i++) {
            const key = pack(colors[i]);
            if (!labByKey.has(key)) labByKey.set(key, colors[i].lab);
        }

        const visited = new Uint8Array(total);
        const groups = [];
        const stack = [];

        for (let idx = 0; idx < total; idx++) {
            if (visited[idx]) continue;
            visited[idx] = 1;

            const baseKey = pack(colors[idx]);
            const baseColor = colors[idx];
            const startRow = (idx / cols) | 0;
            const startCol = idx - startRow * cols;

            let minRow = startRow;
            let maxRow = startRow;
            let minCol = startCol;
            let maxCol = startCol;

            stack.length = 0;
            stack.push(idx);

            while (stack.length) {
                const cur = stack.pop();
                const row = (cur / cols) | 0;
                const col = cur - row * cols;

                if (row < minRow) minRow = row;
                if (row > maxRow) maxRow = row;
                if (col < minCol) minCol = col;
                if (col > maxCol) maxCol = col;

                // 4-neighborhood
                const left = col > 0 ? cur - 1 : -1;
                const right = col < cols - 1 ? cur + 1 : -1;
                const up = row > 0 ? cur - cols : -1;
                const down = row < rows - 1 ? cur + cols : -1;

                if (left >= 0 && !visited[left] && pack(colors[left]) === baseKey) {
                    visited[left] = 1;
                    stack.push(left);
                }
                if (right >= 0 && !visited[right] && pack(colors[right]) === baseKey) {
                    visited[right] = 1;
                    stack.push(right);
                }
                if (up >= 0 && !visited[up] && pack(colors[up]) === baseKey) {
                    visited[up] = 1;
                    stack.push(up);
                }
                if (down >= 0 && !visited[down] && pack(colors[down]) === baseKey) {
                    visited[down] = 1;
                    stack.push(down);
                }
            }

            groups.push({
                key: baseKey,
                r: baseColor.r,
                g: baseColor.g,
                b: baseColor.b,
                minRow,
                maxRow,
                minCol,
                maxCol,
            });
        }

        // Apply rectangles in discovery order; later groups overwrite earlier groups.
        const rectR = new Uint8Array(total);
        const rectG = new Uint8Array(total);
        const rectB = new Uint8Array(total);
        for (let i = 0; i < total; i++) {
            rectR[i] = colors[i].r & 255;
            rectG[i] = colors[i].g & 255;
            rectB[i] = colors[i].b & 255;
        }

        for (const g of groups) {
            for (let y = g.minRow; y <= g.maxRow; y++) {
                let base = y * cols + g.minCol;
                const end = y * cols + g.maxCol;
                for (let i = base; i <= end; i++) {
                    rectR[i] = g.r & 255;
                    rectG[i] = g.g & 255;
                    rectB[i] = g.b & 255;
                }
            }
        }

        // Write back into colors (+ LAB) so downstream similarity/UI reflect rendered output.
        for (let i = 0; i < total; i++) {
            const r = rectR[i];
            const g = rectG[i];
            const b = rectB[i];
            colors[i].r = r;
            colors[i].g = g;
            colors[i].b = b;

            const key = (r << 16) | (g << 8) | b;
            let lab = labByKey.get(key);
            if (!lab) {
                lab = this.rgbToLab(r, g, b);
                labByKey.set(key, lab);
            }
            colors[i].lab = lab;
        }
    }

    clearSource() {
        if (!this.gl) return;

        this.stopVideo();
        this.isVideo = false;
        this.isPaused = false;
        this.image = null;
        this.video = null;
        this.prevCentroids = null;
        this.rawColors = [];
        this.gridData = [];
        this.groupSizes = [];
        this.groupVariance = null;
        this._revealVarianceSmoothed = null;
        this._revealThresholdSmoothed = null;
        this._revealMaskState = null;
        this._alphaMaskSmoothed = null;

        this._ditherDominantKey = null;
        this._ditherSecondaryKey = null;

        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            1,
            1,
            0,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            new Uint8Array([0, 0, 0, 255])
        );

        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        gl.clearColor(0.004, 0.0, 0.012, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
    }

    resetRevealOutputSmoothing() {
        // Keep per-cell variance smoothing (it reduces flicker), but reset the *output* smoothing
        // so UI changes apply instantly (no lag).
        this._revealThresholdSmoothed = null;
        this._revealMaskState = null;
        this._alphaMaskSmoothed = null;
    }

    ensureSamplingCanvasSize() {
        // Sampling only needs grid resolution. Keeping this tiny avoids expensive full-frame reads.
        const w = Math.max(1, Math.floor(this.gridCols));
        const h = Math.max(1, Math.floor(this.gridRows));
        if (this.offCanvas.width !== w || this.offCanvas.height !== h) {
            this.offCanvas.width = w;
            this.offCanvas.height = h;
        }
        if (this.offCtx) {
            this.offCtx.imageSmoothingEnabled = false;
        }
    }
    
    initShaders() {
        const gl = this.gl;
        
        const vertexShaderObj = this.compileShader(gl.VERTEX_SHADER, vertexShader);
        const fragmentShaderObj = this.compileShader(gl.FRAGMENT_SHADER, fragmentShader);
        
        this.program = gl.createProgram();
        gl.attachShader(this.program, vertexShaderObj);
        gl.attachShader(this.program, fragmentShaderObj);
        gl.linkProgram(this.program);
        
        if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) {
            console.error('Program link failed:', gl.getProgramInfoLog(this.program));
        }
        
        this.positionLocation = gl.getAttribLocation(this.program, 'a_position');
        this.texCoordLocation = gl.getAttribLocation(this.program, 'a_texCoord');
        this.resolutionLocation = gl.getUniformLocation(this.program, 'u_resolution');
        this.gridColsLocation = gl.getUniformLocation(this.program, 'u_gridCols');
        this.gridRowsLocation = gl.getUniformLocation(this.program, 'u_gridRows');
        this.gapLocation = gl.getUniformLocation(this.program, 'u_gap');
        this.cornerRadiusLocation = gl.getUniformLocation(this.program, 'u_cornerRadius');
        this.hideSourceLocation = gl.getUniformLocation(this.program, 'u_hideSource');
        this.sourceLightenLocation = gl.getUniformLocation(this.program, 'u_sourceLighten');
        this.sourceGrayscaleLocation = gl.getUniformLocation(this.program, 'u_sourceGrayscale');
        this.bgColorLocation = gl.getUniformLocation(this.program, 'u_bgColor');
        this.imageLocation = gl.getUniformLocation(this.program, 'u_image');
        this.imageResolutionLocation = gl.getUniformLocation(this.program, 'u_imageResolution');
        this.colorMapLocation = gl.getUniformLocation(this.program, 'u_colorMap');
        this.alphaMaskLocation = gl.getUniformLocation(this.program, 'u_alphaMask');
        this.cellMapLocation = gl.getUniformLocation(this.program, 'u_cellMap');
        this.usePackedCellMapLocation = gl.getUniformLocation(this.program, 'u_usePackedCellMap');
        this.useColorMapLocation = gl.getUniformLocation(this.program, 'u_useColorMap');
        this.gradientModeLocation = gl.getUniformLocation(this.program, 'u_gradientMode');
        this.lineModeLocation = gl.getUniformLocation(this.program, 'u_lineMode');
        this.ditherModeLocation = gl.getUniformLocation(this.program, 'u_ditherMode');
        this.dominantColorLocation = gl.getUniformLocation(this.program, 'u_dominantColor');
        this.secondaryColorLocation = gl.getUniformLocation(this.program, 'u_secondaryColor');
        
        this.positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            -1, -1, 1, -1, -1, 1,
            -1, 1, 1, -1, 1, 1
        ]), gl.STATIC_DRAW);
        
        this.texCoordBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
            0, 1, 1, 1, 0, 0,
            0, 0, 1, 1, 1, 0
        ]), gl.STATIC_DRAW);
        
        this.texture = gl.createTexture();
        this.colorMapTexture = gl.createTexture();
        this.alphaMaskTexture = gl.createTexture();
        this.cellMapTexture = gl.createTexture();
    }
    
    compileShader(type, source) {
        const gl = this.gl;
        const shader = gl.createShader(type);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.error('Shader compile failed:', gl.getShaderInfoLog(shader));
            gl.deleteShader(shader);
            return null;
        }
        return shader;
    }
    
    loadImage(img) {
        this.stopVideo();
        this.isVideo = false;
        this.isPaused = false;
        this.image = img;
        this.video = null;
        this.prevCentroids = null;
        
        const gl = this.gl;
        
        const maxWidth = this.canvas.parentElement.clientWidth - 32;
        const aspectRatio = img.height / img.width;
        this.canvas.width = Math.min(img.width, maxWidth);
        this.canvas.height = this.canvas.width * aspectRatio;
        
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        
        // Setup sampling canvas at grid resolution
        this.ensureSamplingCanvasSize();
        
        this.extractRawColors();
        this.applyGrouping();
        this.render();
    }
    
    loadVideo(video) {
        this.stopVideo();
        this.isVideo = true;
        this.isPaused = false;
        this.video = video;
        this.image = null;
        this.prevCentroids = null;
        
        const gl = this.gl;
        
        const maxWidth = this.canvas.parentElement.clientWidth - 32;
        const aspectRatio = video.videoHeight / video.videoWidth;
        this.canvas.width = Math.min(video.videoWidth, maxWidth);
        this.canvas.height = this.canvas.width * aspectRatio;
        
        gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        
        // Setup sampling canvas at grid resolution
        this.ensureSamplingCanvasSize();
        
        // Setup texture parameters
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        
        // Start the render loop
        this.startVideoLoop();
    }
    
    startVideoLoop() {
        const gl = this.gl;
        
        const loop = () => {
            if (!this.isVideo || !this.video || this.isPaused) return;
            
            // Update texture with current video frame
            gl.bindTexture(gl.TEXTURE_2D, this.texture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.video);
            
            // Extract and apply colors
            this.extractRawColors();
            this.applyGrouping();
            this.render();
            
            // Notify UI update
            if (this.onFrameUpdate) {
                this.onFrameUpdate();
            }
            
            this.animationId = requestAnimationFrame(loop);
        };
        
        this.animationId = requestAnimationFrame(loop);
    }

    updateFromCurrentFrameIfPaused() {
        // While paused, we want the still frame to stay fixed, but UI changes
        // should still update the processing and output.
        if (!this.isVideo || !this.video || !this.isPaused) return;
        this.extractRawColors();
        this.applyGrouping();
        this.render();
        if (this.onFrameUpdate) {
            this.onFrameUpdate();
        }
    }
    
    stopVideo() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        if (this.video) {
            this.video.pause();
            // Stop webcam tracks if any
            if (this.video.srcObject) {
                this.video.srcObject.getTracks().forEach(track => track.stop());
                this.video.srcObject = null;
            }
        }
        this.isPaused = false;
    }
    
    async getWebcamDevices() {
        if (!navigator.mediaDevices?.enumerateDevices) {
            return [];
        }
        const devices = await navigator.mediaDevices.enumerateDevices();
        this._webcamDevices = devices.filter(d => d.kind === 'videoinput');
        return this._webcamDevices;
    }

    async getWebcamCameraCount() {
        const devices = await this.getWebcamDevices();
        return devices.length;
    }

    async switchWebcamCamera() {
        // Only meaningful if we're using a live stream
        const devices = await this.getWebcamDevices();
        if (!devices || devices.length < 2) {
            throw new Error('No alternate camera found');
        }

        const currentId = this._webcamDeviceId;
        let currentIndex = devices.findIndex(d => d.deviceId && d.deviceId === currentId);
        if (currentIndex < 0) currentIndex = 0;
        const nextIndex = (currentIndex + 1) % devices.length;
        const next = devices[nextIndex];

        await this.loadWebcam({ deviceId: next.deviceId });
    }

    async loadWebcam(options = {}) {
        this.stopVideo();
        this.prevCentroids = null;
        this.isPaused = false;
        
        try {
            const { deviceId, facingMode } = options;
            const videoConstraints = {
                width: { ideal: 1280 },
                height: { ideal: 720 },
                ...(deviceId
                    ? { deviceId: { exact: deviceId } }
                    : { facingMode: facingMode || 'user' })
            };

            const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints });
            const track = stream.getVideoTracks()[0];
            const settings = track?.getSettings?.() || {};
            this._webcamDeviceId = settings.deviceId || deviceId || null;
            // Refresh list after permission so deviceIds/labels are available.
            await this.getWebcamDevices();
            
            const video = document.createElement('video');
            video.srcObject = stream;
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            
            return new Promise((resolve, reject) => {
                video.oncanplay = async () => {
                    // Remove handler so it doesn't fire again
                    video.oncanplay = null;
                    
                    try {
                        await video.play();
                        
                        this.isVideo = true;
                        this.video = video;
                        this.image = null;
                        
                        const gl = this.gl;
                        
                        const maxWidth = this.canvas.parentElement.clientWidth - 32;
                        const aspectRatio = video.videoHeight / video.videoWidth;
                        this.canvas.width = Math.min(video.videoWidth, maxWidth);
                        this.canvas.height = this.canvas.width * aspectRatio;
                        
                        gl.viewport(0, 0, this.canvas.width, this.canvas.height);

                        this.ensureSamplingCanvasSize();
                        
                        gl.bindTexture(gl.TEXTURE_2D, this.texture);
                        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
                        
                        this.startVideoLoop();
                        resolve();
                    } catch (e) {
                        reject(e);
                    }
                };
                video.onerror = reject;
            });
        } catch (err) {
            console.error('Webcam error:', err);
            throw err;
        }
    }
    
    extractRawColors() {
        const source = this.isVideo ? this.video : this.image;
        if (!source) return;

        // Draw into a tiny grid-sized canvas so we only ever read gridCols*gridRows pixels.
        this.ensureSamplingCanvasSize();
        this.offCtx.drawImage(source, 0, 0, this.offCanvas.width, this.offCanvas.height);

        const width = this.offCanvas.width;
        const height = this.offCanvas.height;
        const imageData = this.offCtx.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        // Keep previous colors for smoothing
        const prevColors = this.rawColors;
        this.rawColors = [];
        
        // Smoothing factor: lower = more smoothing/lag, higher = more responsive
        // 0.15 provides a good balance for webcam to reduce flickering
        const lerp = 0.15;
        
        for (let row = 0; row < this.gridRows; row++) {
            for (let col = 0; col < this.gridCols; col++) {
                // Since the sampling canvas is exactly grid-sized, each pixel maps to a cell.
                const idx = (row * width + col) * 4;
                
                let r = data[idx];
                let g = data[idx + 1];
                let b = data[idx + 2];
                
                // Apply temporal smoothing if we have previous data and it's video
                if (this.isVideo && prevColors.length === this.gridRows * this.gridCols) {
                    const prevIndex = row * this.gridCols + col;
                    const prev = prevColors[prevIndex];
                    
                    r = prev.r + (r - prev.r) * lerp;
                    g = prev.g + (g - prev.g) * lerp;
                    b = prev.b + (b - prev.b) * lerp;
                }
                
                this.rawColors.push({
                    r: r,
                    g: g,
                    b: b,
                    row,
                    col
                });
            }
        }
    }

    // In-place selection (average O(n)) to avoid sorting for percentiles.
    // Returns the k-th smallest element.
    quickSelect(arr, k) {
        let left = 0;
        let right = arr.length - 1;
        while (left <= right) {
            const pivotIndex = left + Math.floor(Math.random() * (right - left + 1));
            const pivotValue = arr[pivotIndex];
            // Move pivot to end
            arr[pivotIndex] = arr[right];
            arr[right] = pivotValue;

            let storeIndex = left;
            for (let i = left; i < right; i++) {
                if (arr[i] < pivotValue) {
                    const tmp = arr[storeIndex];
                    arr[storeIndex] = arr[i];
                    arr[i] = tmp;
                    storeIndex++;
                }
            }
            // Move pivot to its final place
            arr[right] = arr[storeIndex];
            arr[storeIndex] = pivotValue;

            if (storeIndex === k) return arr[storeIndex];
            if (storeIndex < k) left = storeIndex + 1;
            else right = storeIndex - 1;
        }
        return arr[k];
    }
    
    rgbToLab(r, g, b) {
        let r_ = r / 255, g_ = g / 255, b_ = b / 255;
        
        r_ = (r_ > 0.04045) ? Math.pow((r_ + 0.055) / 1.055, 2.4) : r_ / 12.92;
        g_ = (g_ > 0.04045) ? Math.pow((g_ + 0.055) / 1.055, 2.4) : g_ / 12.92;
        b_ = (b_ > 0.04045) ? Math.pow((b_ + 0.055) / 1.055, 2.4) : b_ / 12.92;
        
        let x = (r_ * 0.4124 + g_ * 0.3576 + b_ * 0.1805) / 0.95047;
        let y = (r_ * 0.2126 + g_ * 0.7152 + b_ * 0.0722) / 1.00000;
        let z = (r_ * 0.0193 + g_ * 0.1192 + b_ * 0.9505) / 1.08883;
        
        x = (x > 0.008856) ? Math.pow(x, 1/3) : (7.787 * x) + 16/116;
        y = (y > 0.008856) ? Math.pow(y, 1/3) : (7.787 * y) + 16/116;
        z = (z > 0.008856) ? Math.pow(z, 1/3) : (7.787 * z) + 16/116;
        
        return {
            l: (116 * y) - 16,
            a: 500 * (x - y),
            b: 200 * (y - z)
        };
    }

    labToRgb(l, a, b) {
        let y = (l + 16) / 116;
        let x = a / 500 + y;
        let z = y - b / 200;
        
        let x3 = x * x * x;
        let y3 = y * y * y;
        let z3 = z * z * z;
        
        x = (x3 > 0.008856) ? x3 : (x - 16/116) / 7.787;
        y = (y3 > 0.008856) ? y3 : (y - 16/116) / 7.787;
        z = (z3 > 0.008856) ? z3 : (z - 16/116) / 7.787;
        
        x = x * 0.95047;
        y = y * 1.00000;
        z = z * 1.08883;
        
        let r = x * 3.2406 + y * -1.5372 + z * -0.4986;
        let g = x * -0.9689 + y * 1.8758 + z * 0.0415;
        let bl = x * 0.0557 + y * -0.2040 + z * 1.0570;
        
        r = (r > 0.0031308) ? (1.055 * Math.pow(r, 1/2.4) - 0.055) : 12.92 * r;
        g = (g > 0.0031308) ? (1.055 * Math.pow(g, 1/2.4) - 0.055) : 12.92 * g;
        bl = (bl > 0.0031308) ? (1.055 * Math.pow(bl, 1/2.4) - 0.055) : 12.92 * bl;
        
        return {
            r: Math.max(0, Math.min(255, Math.round(r * 255))),
            g: Math.max(0, Math.min(255, Math.round(g * 255))),
            b: Math.max(0, Math.min(255, Math.round(bl * 255)))
        };
    }

    colorDistance(c1, c2) {
        const lab1 = this.rgbToLab(c1.r, c1.g, c1.b);
        const lab2 = this.rgbToLab(c2.r, c2.g, c2.b);
        const dl = lab1.l - lab2.l;
        const da = lab1.a - lab2.a;
        const db = lab1.b - lab2.b;
        return Math.sqrt(dl * dl + da * da + db * db);
    }
    
    kMeans(colors, k) {
        let centroids = [];
        
        // Ensure colors have LAB values (optimization)
        if (colors.length > 0 && !colors[0].lab) {
            for(let i=0; i<colors.length; i++) {
                colors[i].lab = this.rgbToLab(colors[i].r, colors[i].g, colors[i].b);
            }
        }
        
        // 1. Initialization
        // Use previous centroids as starting point for video stability (Warm Start)
        if (this.isVideo && this.prevCentroids && this.prevCentroids.length === k) {
            centroids = this.prevCentroids.map(c => ({
                ...c,
                lab: {...c.lab} // Ensure deep copy of LAB
            }));
        } else {
            // Fallback to initialization from data
            if (colors.length <= k) {
                return { 
                    centroids: colors.map(c => ({...c})), 
                    assignments: colors.map((_, i) => i) 
                };
            }
            
            // Pick k distributed colors as initial centroids
            const step = Math.floor(colors.length / k);
            for(let i=0; i<k; i++) {
                const c = colors[i * step];
                centroids.push({
                    r: c.r, g: c.g, b: c.b,
                    lab: {...c.lab}
                });
            }
        }

        let assignments = new Array(colors.length).fill(0);
        let changed = true;
        let iterations = 0;
        
        const useRgbDistance = this.extractionMode === 'rgb-kmeans';

        while(changed && iterations < 10) { // Limit iterations for performance
            changed = false;
            // Assign points to clusters
            for(let i=0; i<colors.length; i++) {
                let minDistSq = Infinity;
                let clusterIdx = -1;
                const p = colors[i];
                const pLab = p.lab;
                
                for(let j=0; j<centroids.length; j++) {
                    let dSq;
                    if (useRgbDistance) {
                        const dr = p.r - centroids[j].r;
                        const dg = p.g - centroids[j].g;
                        const db = p.b - centroids[j].b;
                        dSq = dr * dr + dg * dg + db * db;
                    } else {
                        const cLab = centroids[j].lab;
                        const dl = pLab.l - cLab.l;
                        const da = pLab.a - cLab.a;
                        const db = pLab.b - cLab.b;
                        dSq = dl*dl + da*da + db*db;
                    }
                    
                    if (dSq < minDistSq) {
                        minDistSq = dSq;
                        clusterIdx = j;
                    }
                }
                if (assignments[i] !== clusterIdx) {
                    assignments[i] = clusterIdx;
                    changed = true;
                }
            }
            
            // Update centroids
            if (useRgbDistance) {
                const sums = Array(k).fill(0).map(() => ({r:0, g:0, b:0, count:0}));
                for(let i=0; i<colors.length; i++) {
                    const c = colors[i];
                    const clusterIdx = assignments[i];
                    sums[clusterIdx].r += c.r;
                    sums[clusterIdx].g += c.g;
                    sums[clusterIdx].b += c.b;
                    sums[clusterIdx].count++;
                }

                for(let j=0; j<k; j++) {
                    if (sums[j].count > 0) {
                        const avgR = sums[j].r / sums[j].count;
                        const avgG = sums[j].g / sums[j].count;
                        const avgB = sums[j].b / sums[j].count;
                        centroids[j].r = avgR;
                        centroids[j].g = avgG;
                        centroids[j].b = avgB;
                        centroids[j].lab = this.rgbToLab(avgR, avgG, avgB);
                    }
                }
            } else {
                const sums = Array(k).fill(0).map(() => ({l:0, a:0, b:0, count:0}));
                for(let i=0; i<colors.length; i++) {
                    const lab = colors[i].lab;
                    const clusterIdx = assignments[i];
                    sums[clusterIdx].l += lab.l;
                    sums[clusterIdx].a += lab.a;
                    sums[clusterIdx].b += lab.b;
                    sums[clusterIdx].count++;
                }

                for(let j=0; j<k; j++) {
                    if (sums[j].count > 0) {
                        const avgL = sums[j].l / sums[j].count;
                        const avgA = sums[j].a / sums[j].count;
                        const avgB = sums[j].b / sums[j].count;

                        centroids[j].lab = { l: avgL, a: avgA, b: avgB };
                        const rgb = this.labToRgb(avgL, avgA, avgB);
                        centroids[j].r = rgb.r;
                        centroids[j].g = rgb.g;
                        centroids[j].b = rgb.b;
                    }
                }
            }
            iterations++;
        }
        
        // Store centroids for next frame if video
        if (this.isVideo) {
            // Apply temporal smoothing to centroids to reduce jitter
            if (this.prevCentroids && this.prevCentroids.length === k) {
                const alpha = 0.3; // 30% new, 70% old - heavy smoothing for stability
                for(let i=0; i<k; i++) {
                    centroids[i].r = centroids[i].r * alpha + this.prevCentroids[i].r * (1 - alpha);
                    centroids[i].g = centroids[i].g * alpha + this.prevCentroids[i].g * (1 - alpha);
                    centroids[i].b = centroids[i].b * alpha + this.prevCentroids[i].b * (1 - alpha);
                    
                    // Smooth LAB as well
                    centroids[i].lab.l = centroids[i].lab.l * alpha + this.prevCentroids[i].lab.l * (1 - alpha);
                    centroids[i].lab.a = centroids[i].lab.a * alpha + this.prevCentroids[i].lab.a * (1 - alpha);
                    centroids[i].lab.b = centroids[i].lab.b * alpha + this.prevCentroids[i].lab.b * (1 - alpha);
                }
            }
            this.prevCentroids = centroids.map(c => ({
                ...c,
                lab: {...c.lab}
            }));
        } else {
            this.prevCentroids = null;
        }
        
        return { centroids, assignments };
    }

    gifencQuantizeToCentroids(colors, k) {
        const count = colors.length;
        if (!count) {
            return [{ r: 0, g: 0, b: 0, lab: this.rgbToLab(0, 0, 0) }];
        }

        const target = Math.max(2, k | 0);
        const rgba = new Uint8Array(count * 4);
        for (let i = 0; i < count; i++) {
            const c = colors[i];
            const base = i * 4;
            rgba[base + 0] = Math.max(0, Math.min(255, Math.round(c.r)));
            rgba[base + 1] = Math.max(0, Math.min(255, Math.round(c.g)));
            rgba[base + 2] = Math.max(0, Math.min(255, Math.round(c.b)));
            rgba[base + 3] = 255;
        }

        // Reduce noise prior to palette building (helps stability on video/webcam).
        gifencPrequantize(rgba);

        const palette = gifencQuantize(rgba, target, { format: 'rgb565' });
        if (!palette || !palette.length) {
            const c = colors[0];
            const lab = c.lab || this.rgbToLab(c.r, c.g, c.b);
            return [{ r: c.r, g: c.g, b: c.b, lab }];
        }

        const centroids = palette.map((p) => ({
            r: p[0],
            g: p[1],
            b: p[2],
            lab: this.rgbToLab(p[0], p[1], p[2]),
        }));

        // Temporal smoothing (matches kMeans smoothing) to reduce palette jitter on video.
        if (this.isVideo) {
            if (this.prevCentroids && this.prevCentroids.length === centroids.length) {
                const alpha = 0.3;
                for (let i = 0; i < centroids.length; i++) {
                    const prev = this.prevCentroids[i];
                    const cur = centroids[i];
                    cur.r = cur.r * alpha + prev.r * (1 - alpha);
                    cur.g = cur.g * alpha + prev.g * (1 - alpha);
                    cur.b = cur.b * alpha + prev.b * (1 - alpha);
                    cur.lab.l = cur.lab.l * alpha + prev.lab.l * (1 - alpha);
                    cur.lab.a = cur.lab.a * alpha + prev.lab.a * (1 - alpha);
                    cur.lab.b = cur.lab.b * alpha + prev.lab.b * (1 - alpha);
                }
            }
            this.prevCentroids = centroids.map((c) => ({ ...c, lab: { ...c.lab } }));
        } else {
            this.prevCentroids = null;
        }

        return centroids;
    }

    applyGrouping() {
        if (!this.rawColors.length) return;
        
        const colors = this.rawColors.map(c => ({ ...c }));
        
        // Pre-calculate LAB for all colors (Optimization)
        for(let i=0; i<colors.length; i++) {
            colors[i].lab = this.rgbToLab(colors[i].r, colors[i].g, colors[i].b);
        }

        const mode = this.extractionMode;
        const useGifenc = mode === 'gifenc';
        const useStandardRgb = mode === 'rgb';
        const useRgbKMeans = mode === 'rgb-kmeans' || useGifenc;
        const useLabKMeans = !useStandardRgb && !useRgbKMeans;

        let centroids = null;
        if (!useStandardRgb) {
            // 1. Get Target Palette (Centroids)
            if (useGifenc) {
                centroids = this.gifencQuantizeToCentroids(colors, this.targetColors);
            } else {
                ({ centroids } = this.kMeans(colors, this.targetColors));
            }
        }
        
        // 2. Quadtree grouping with percentile thresholding
        // Instead of mapping 0-100 to a fixed absolute variance range (which can collapse to 1 color
        // depending on content and other settings), we compute variances for all quadtree nodes and
        // use the slider as a percentile over that distribution.

        this.groupSizes = new Array(colors.length).fill(1);
        this.groupVariance = new Float32Array(colors.length);

        const cols = this.gridCols | 0;
        const rows = this.gridRows | 0;
        const stride = cols + 1;
        const integralSize = stride * (rows + 1);

        // Build integral images for fast rectangular stats (O(1) per rect).
        // LAB mode: stats and palette matching are in LAB.
        // RGB mode: stats are in RGB (no k-means); quadtree merges use mean RGB.
        let rectSum;
        let getRectStats;

        if (useStandardRgb || useRgbKMeans) {
            if (!this._integralRgb || this._integralRgb.size !== integralSize) {
                this._integralRgb = {
                    size: integralSize,
                    cols,
                    rows,
                    sumR: new Float32Array(integralSize),
                    sumG: new Float32Array(integralSize),
                    sumB: new Float32Array(integralSize),
                    sumSq: new Float32Array(integralSize),
                };
            }

            const { sumR, sumG, sumB, sumSq } = this._integralRgb;
            sumR.fill(0);
            sumG.fill(0);
            sumB.fill(0);
            sumSq.fill(0);

            for (let y = 1; y <= rows; y++) {
                const rowBase = y * stride;
                const prevRowBase = (y - 1) * stride;
                const cellRowBase = (y - 1) * cols;
                for (let x = 1; x <= cols; x++) {
                    const cellIndex = cellRowBase + (x - 1);
                    const c = colors[cellIndex];
                    const magSq = c.r * c.r + c.g * c.g + c.b * c.b;

                    const idx = rowBase + x;
                    const idxLeft = idx - 1;
                    const idxUp = prevRowBase + x;
                    const idxUpLeft = idxUp - 1;

                    sumR[idx] = c.r + sumR[idxLeft] + sumR[idxUp] - sumR[idxUpLeft];
                    sumG[idx] = c.g + sumG[idxLeft] + sumG[idxUp] - sumG[idxUpLeft];
                    sumB[idx] = c.b + sumB[idxLeft] + sumB[idxUp] - sumB[idxUpLeft];
                    sumSq[idx] = magSq + sumSq[idxLeft] + sumSq[idxUp] - sumSq[idxUpLeft];
                }
            }

            rectSum = (arr, x0, y0, x1, y1) => {
                const i11 = y1 * stride + x1;
                const i10 = y1 * stride + x0;
                const i01 = y0 * stride + x1;
                const i00 = y0 * stride + x0;
                return arr[i11] - arr[i10] - arr[i01] + arr[i00];
            };

            getRectStats = (x, y, w, h) => {
                const x0 = x;
                const y0 = y;
                const x1 = x + w;
                const y1 = y + h;
                const count = w * h;

                const sR = rectSum(sumR, x0, y0, x1, y1);
                const sG = rectSum(sumG, x0, y0, x1, y1);
                const sB = rectSum(sumB, x0, y0, x1, y1);
                const sSq = rectSum(sumSq, x0, y0, x1, y1);

                const meanR = sR / count;
                const meanG = sG / count;
                const meanB = sB / count;

                const meanSqMag = meanR * meanR + meanG * meanG + meanB * meanB;
                const variance = (sSq / count) - meanSqMag;

                const meanLab = this.rgbToLab(meanR, meanG, meanB);
                return {
                    meanRgb: { r: meanR, g: meanG, b: meanB },
                    meanLab,
                    variance: Math.max(0, variance),
                };
            };
        } else {
            if (!this._integralLab || this._integralLab.size !== integralSize) {
                this._integralLab = {
                    size: integralSize,
                    cols,
                    rows,
                    sumL: new Float32Array(integralSize),
                    sumA: new Float32Array(integralSize),
                    sumB: new Float32Array(integralSize),
                    sumSq: new Float32Array(integralSize),
                };
            }

            const { sumL, sumA, sumB, sumSq } = this._integralLab;
            sumL.fill(0);
            sumA.fill(0);
            sumB.fill(0);
            sumSq.fill(0);

            for (let y = 1; y <= rows; y++) {
                const rowBase = y * stride;
                const prevRowBase = (y - 1) * stride;
                const cellRowBase = (y - 1) * cols;
                for (let x = 1; x <= cols; x++) {
                    const cellIndex = cellRowBase + (x - 1);
                    const lab = colors[cellIndex].lab;
                    const magSq = lab.l * lab.l + lab.a * lab.a + lab.b * lab.b;

                    const idx = rowBase + x;
                    const idxLeft = idx - 1;
                    const idxUp = prevRowBase + x;
                    const idxUpLeft = idxUp - 1;

                    sumL[idx] = lab.l + sumL[idxLeft] + sumL[idxUp] - sumL[idxUpLeft];
                    sumA[idx] = lab.a + sumA[idxLeft] + sumA[idxUp] - sumA[idxUpLeft];
                    sumB[idx] = lab.b + sumB[idxLeft] + sumB[idxUp] - sumB[idxUpLeft];
                    sumSq[idx] = magSq + sumSq[idxLeft] + sumSq[idxUp] - sumSq[idxUpLeft];
                }
            }

            rectSum = (arr, x0, y0, x1, y1) => {
                const i11 = y1 * stride + x1;
                const i10 = y1 * stride + x0;
                const i01 = y0 * stride + x1;
                const i00 = y0 * stride + x0;
                return arr[i11] - arr[i10] - arr[i01] + arr[i00];
            };

            getRectStats = (x, y, w, h) => {
                const x0 = x;
                const y0 = y;
                const x1 = x + w;
                const y1 = y + h;
                const count = w * h;

                const sL = rectSum(sumL, x0, y0, x1, y1);
                const sA = rectSum(sumA, x0, y0, x1, y1);
                const sB = rectSum(sumB, x0, y0, x1, y1);
                const sSq = rectSum(sumSq, x0, y0, x1, y1);

                const meanL = sL / count;
                const meanA = sA / count;
                const meanB = sB / count;

                const meanSqMag = meanL * meanL + meanA * meanA + meanB * meanB;
                const variance = (sSq / count) - meanSqMag;

                return {
                    mean: { l: meanL, a: meanA, b: meanB },
                    variance: Math.max(0, variance),
                };
            };
        }
        const findNearestCentroid = useStandardRgb
            ? null
            : (value) => {
                let minDistSq = Infinity;
                let nearest = centroids[0];
                for (const c of centroids) {
                    let dSq;
                    if (useRgbKMeans) {
                        const dr = value.r - c.r;
                        const dg = value.g - c.g;
                        const db = value.b - c.b;
                        dSq = dr * dr + dg * dg + db * db;
                    } else {
                        const dl = value.l - c.lab.l;
                        const da = value.a - c.lab.a;
                        const db = value.b - c.lab.b;
                        dSq = dl * dl + da * da + db * db;
                    }
                    if (dSq < minDistSq) {
                        minDistSq = dSq;
                        nearest = c;
                    }
                }
                return nearest;
            };

        const fillRect = (x, y, w, h, color, variance) => {
            const size = w * h;
            for(let iy=y; iy<y+h; iy++) {
                for(let ix=x; ix<x+w; ix++) {
                    const idx = iy * this.gridCols + ix;
                    colors[idx].r = color.r;
                    colors[idx].g = color.g;
                    colors[idx].b = color.b;
                    colors[idx].lab = color.lab;
                    this.groupSizes[idx] = size;
                    this.groupVariance[idx] = variance;
                }
            }
        };

        const nodeVariances = [];

        const buildNode = (x, y, w, h) => {
            if (w <= 0 || h <= 0) return null;
            const stats = getRectStats(x, y, w, h);
            const node = (useStandardRgb || useRgbKMeans)
                ? { x, y, w, h, meanRgb: stats.meanRgb, meanLab: stats.meanLab, variance: stats.variance, children: null }
                : { x, y, w, h, mean: stats.mean, variance: stats.variance, children: null };

            // Exclude single-cell nodes from the distribution to avoid skewing percentiles.
            if (w * h > 1) nodeVariances.push(stats.variance);

            if (w === 1 && h === 1) return node;

            if (w > 1 && h > 1) {
                const halfW = Math.floor(w / 2);
                const halfH = Math.floor(h / 2);
                node.children = [
                    buildNode(x, y, halfW, halfH),
                    buildNode(x + halfW, y, w - halfW, halfH),
                    buildNode(x, y + halfH, halfW, h - halfH),
                    buildNode(x + halfW, y + halfH, w - halfW, h - halfH),
                ];
            } else if (w > 1) {
                const halfW = Math.floor(w / 2);
                node.children = [
                    buildNode(x, y, halfW, h),
                    buildNode(x + halfW, y, w - halfW, h),
                ];
            } else {
                const halfH = Math.floor(h / 2);
                node.children = [
                    buildNode(x, y, w, halfH),
                    buildNode(x, y + halfH, w, h - halfH),
                ];
            }

            return node;
        };

        const root = buildNode(0, 0, cols, rows);

        // Map groupThreshold 0-100 to a percentile over the quadtree node variance distribution.
        let mergeVarianceThreshold = -1;
        if (this.groupThreshold >= 100) {
            mergeVarianceThreshold = Infinity;
        } else if (this.groupThreshold > 0 && nodeVariances.length > 0) {
            const n = nodeVariances.length;
            const index = Math.floor((this.groupThreshold / 100) * (n - 1));
            if (!this._groupVarianceScratch || this._groupVarianceScratch.length !== n) {
                this._groupVarianceScratch = new Float32Array(n);
            }
            for (let i = 0; i < n; i++) this._groupVarianceScratch[i] = nodeVariances[i];
            mergeVarianceThreshold = this.quickSelect(this._groupVarianceScratch, index);
        }

        const applyNode = (node, parentVariance = 5000) => {
            if (!node) return;

            if (node.w === 1 && node.h === 1) {
                const idx = node.y * this.gridCols + node.x;
                if (useStandardRgb) {
                    // Use parent variance to allow progressive reveal of details
                    fillRect(node.x, node.y, 1, 1, { r: colors[idx].r, g: colors[idx].g, b: colors[idx].b, lab: colors[idx].lab }, parentVariance);
                } else {
                    const nearest = useRgbKMeans
                        ? findNearestCentroid({ r: colors[idx].r, g: colors[idx].g, b: colors[idx].b })
                        : findNearestCentroid(colors[idx].lab);
                    // Use parent variance to allow progressive reveal of details
                    fillRect(node.x, node.y, 1, 1, nearest, parentVariance);
                }
                return;
            }

            if (node.variance <= mergeVarianceThreshold) {
                if (useStandardRgb) {
                    const meanRgb = node.meanRgb;
                    fillRect(
                        node.x,
                        node.y,
                        node.w,
                        node.h,
                        { r: meanRgb.r, g: meanRgb.g, b: meanRgb.b, lab: node.meanLab },
                        node.variance
                    );
                } else {
                    if (useRgbKMeans) {
                        const meanRgb = node.meanRgb;
                        const nearest = findNearestCentroid(meanRgb);
                        fillRect(node.x, node.y, node.w, node.h, nearest, node.variance);
                    } else {
                        const nearest = findNearestCentroid(node.mean);
                        fillRect(node.x, node.y, node.w, node.h, nearest, node.variance);
                    }
                }
                return;
            }

            const children = node.children || [];
            for (const child of children) {
                applyNode(child, node.variance);
            }
        };

        applyNode(root);

        // Group-rect mode: when grouping threshold is active (>0), expand each connected
        // same-color region into its full bounding box (draw order defines overlap).
        if (this.groupThreshold > 0 && this.groupRectMode) {
            this.applyGroupRectMode(colors, cols, rows);
        }

        // Calculate neighbor similarity for each cell (independent of grouping)
        // This is used for reveal mode - higher value = more similar to neighbors
        this.neighborSimilarity = [];
        for (let i = 0; i < colors.length; i++) {
            const row = Math.floor(i / this.gridCols);
            const col = i % this.gridCols;
            const lab = colors[i].lab;
            
            let totalSimilarity = 0;
            let neighborCount = 0;
            
            // Check all 4 neighbors
            const neighbors = [
                col > 0 ? i - 1 : -1,                          // left
                col < this.gridCols - 1 ? i + 1 : -1,          // right
                row > 0 ? i - this.gridCols : -1,              // top
                row < this.gridRows - 1 ? i + this.gridCols : -1  // bottom
            ];
            
            for (const ni of neighbors) {
                if (ni >= 0 && ni < colors.length) {
                    const nLab = colors[ni].lab;
                    const dl = lab.l - nLab.l;
                    const da = lab.a - nLab.a;
                    const db = lab.b - nLab.b;
                    // We can use sqrt here as it's only 4 times per pixel
                    const dist = Math.sqrt(dl*dl + da*da + db*db);
                    
                    // Convert distance to similarity (0-1, where 1 = identical)
                    // Max possible distance in LAB is ~100-150 usually.
                    // Let's use 100 as the "max meaningful distance" for similarity.
                    totalSimilarity += 1 - Math.min(1, dist / 100);
                    neighborCount++;
                }
            }
            
            // Average similarity to neighbors (0-1)
            this.neighborSimilarity[i] = neighborCount > 0 ? totalSimilarity / neighborCount : 0;
        }
        
        this.gridData = [];
        const cellWidth = this.canvas.width / this.gridCols;
        const cellHeight = this.canvas.height / this.gridRows;
        
        for (let i = 0; i < colors.length; i++) {
            const color = colors[i];
            const row = color.row;
            const col = color.col;
            
            const gapPx = this.gap * cellWidth * 0.5;
            const x = Math.round(col * cellWidth + gapPx);
            const y = Math.round(row * cellHeight + gapPx);
            const width = Math.round(cellWidth - gapPx * 2);
            const height = Math.round(cellHeight - gapPx * 2);
            
            this.gridData.push({
                row,
                col,
                x,
                y,
                width,
                height,
                groupSize: this.groupSizes[i],
                similarity: this.neighborSimilarity[i],
                color: {
                    r: Math.round(color.r),
                    g: Math.round(color.g),
                    b: Math.round(color.b),
                    hex: this.rgbToHex(color.r, color.g, color.b)
                }
            });
        }
        
        this.updateColorMapTexture();

        // Stabilize reveal mask on video/webcam to avoid flicker.
        if (this.isVideo && this.groupVariance && this.groupVariance.length) {
            const n = this.groupVariance.length;
            if (!this._revealVarianceSmoothed || this._revealVarianceSmoothed.length !== n) {
                this._revealVarianceSmoothed = new Float32Array(n);
                this._revealVarianceSmoothed.set(this.groupVariance);
            } else {
                const a = this.revealVarianceLerp;
                const smoothed = this._revealVarianceSmoothed;
                const current = this.groupVariance;
                for (let i = 0; i < n; i++) {
                    smoothed[i] = smoothed[i] + (current[i] - smoothed[i]) * a;
                }
            }
        } else {
            this._revealVarianceSmoothed = null;
        }

        this.updateAlphaMaskTexture();
    }
    
    updateColorMapTexture() {
        const gl = this.gl;
        const totalCells = this.gridCols * this.gridRows;
        const colorData = new Uint8Array(totalCells * 4);

        const counts = new Map();
        
        for (let i = 0; i < this.gridData.length; i++) {
            const color = this.gridData[i].color;
            colorData[i * 4] = color.r;
            colorData[i * 4 + 1] = color.g;
            colorData[i * 4 + 2] = color.b;
            colorData[i * 4 + 3] = 255;

            const key = ((color.r & 255) << 16) | ((color.g & 255) << 8) | (color.b & 255);
            const next = (counts.get(key) || 0) + 1;
            counts.set(key, next);
        }

        // Pick top-2 keys by count.
        let top1Key = null;
        let top1Count = 0;
        let top2Key = null;
        let top2Count = 0;
        for (const [key, count] of counts.entries()) {
            if (count > top1Count) {
                top2Key = top1Key;
                top2Count = top1Count;
                top1Key = key;
                top1Count = count;
            } else if (key !== top1Key && count > top2Count) {
                top2Key = key;
                top2Count = count;
            }
        }

        const total = totalCells;
        const switchMargin = Math.max(2, Math.floor(total * 0.02));

        // Stabilize dominant key
        if (this._ditherDominantKey == null) {
            this._ditherDominantKey = top1Key;
        } else {
            const locked = this._ditherDominantKey;
            const lockedCount = counts.get(locked) || 0;
            if (top1Key != null && top1Key !== locked && top1Count >= lockedCount + switchMargin) {
                this._ditherDominantKey = top1Key;
            }
            // If locked vanished, allow immediate switch
            if ((counts.get(this._ditherDominantKey) || 0) === 0 && top1Key != null) {
                this._ditherDominantKey = top1Key;
            }
        }

        const dominantKey = this._ditherDominantKey;

        // Choose best secondary excluding dominant
        let bestSecondaryKey = null;
        let bestSecondaryCount = 0;
        for (const [key, count] of counts.entries()) {
            if (key === dominantKey) continue;
            if (count > bestSecondaryCount) {
                bestSecondaryCount = count;
                bestSecondaryKey = key;
            }
        }

        // Stabilize secondary key
        if (this._ditherSecondaryKey == null || this._ditherSecondaryKey === dominantKey) {
            this._ditherSecondaryKey = bestSecondaryKey;
        } else {
            const locked = this._ditherSecondaryKey;
            const lockedCount = counts.get(locked) || 0;
            const candCount = bestSecondaryKey != null ? (counts.get(bestSecondaryKey) || 0) : 0;
            if (bestSecondaryKey != null && bestSecondaryKey !== locked && candCount >= lockedCount + switchMargin) {
                this._ditherSecondaryKey = bestSecondaryKey;
            }
            if ((counts.get(this._ditherSecondaryKey) || 0) === 0) {
                this._ditherSecondaryKey = bestSecondaryKey;
            }
        }

        const secondaryKey = (this._ditherSecondaryKey != null) ? this._ditherSecondaryKey : dominantKey;

        if (dominantKey != null) {
            const r = (dominantKey >> 16) & 255;
            const g = (dominantKey >> 8) & 255;
            const b = dominantKey & 255;
            this.dominantGridColor = { r: r / 255, g: g / 255, b: b / 255 };
        }

        if (secondaryKey != null) {
            const r = (secondaryKey >> 16) & 255;
            const g = (secondaryKey >> 8) & 255;
            const b = secondaryKey & 255;
            this.secondaryGridColor = { r: r / 255, g: g / 255, b: b / 255 };
        } else {
            this.secondaryGridColor = this.dominantGridColor;
        }
        
        gl.bindTexture(gl.TEXTURE_2D, this.colorMapTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.gridCols, this.gridRows, 0, gl.RGBA, gl.UNSIGNED_BYTE, colorData);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    }
    
    updateAlphaMaskTexture() {
        const gl = this.gl;
        const totalCells = this.gridCols * this.gridRows;
        const alphaData = new Uint8Array(totalCells * 4);
        const cellMapData = new Uint8Array(totalCells * 4);

        const varianceSource = (this._revealVarianceSmoothed && this._revealVarianceSmoothed.length)
            ? this._revealVarianceSmoothed
            : this.groupVariance;
        
        let threshold = -1;
        let thresholdComputed = false;
        
        // Calculate dynamic threshold based on percentile.
        // Default behavior reveals the flattest (lowest-variance) regions as revealSize increases.
        // "Reveal Details" flips this to reveal the most detailed (highest-variance) regions.
        if (this.revealSize <= 0) {
            // No reveal at 0%.
            threshold = this.revealDetails ? Infinity : -Infinity;
            thresholdComputed = true;
        } else if (this.revealSize >= 100) {
            threshold = this.revealDetails ? -Infinity : Infinity;
            thresholdComputed = true;
        } else if (varianceSource && varianceSource.length > 0) {
            const n = varianceSource.length;
            const t = this.revealSize / 100;
            const index = this.revealDetails
                ? Math.floor((1 - t) * (n - 1))
                : Math.floor(t * (n - 1));

            if (!this._varianceScratch || this._varianceScratch.length !== n) {
                this._varianceScratch = new Float32Array(n);
            }
            this._varianceScratch.set(varianceSource);
            threshold = this.quickSelect(this._varianceScratch, index);
            thresholdComputed = true;
        }

        // Smooth threshold on video/webcam to reduce cutoff jitter.
        let thresholdUsed = threshold;
        if (this.isVideo && thresholdComputed && Number.isFinite(threshold)) {
            if (this._revealThresholdSmoothed == null) {
                this._revealThresholdSmoothed = threshold;
            } else {
                this._revealThresholdSmoothed =
                    this._revealThresholdSmoothed + (threshold - this._revealThresholdSmoothed) * this.revealThresholdLerp;
            }
            thresholdUsed = this._revealThresholdSmoothed;
        } else if (!this.isVideo) {
            this._revealThresholdSmoothed = null;
        }

        // Hysteresis band in variance units (video/webcam only).
        let hysteresisBand = 0;
        if (this.isVideo && varianceSource && varianceSource.length > 0 && this.revealHysteresis > 0) {
            let vMin = Infinity;
            let vMax = -Infinity;
            for (let i = 0; i < varianceSource.length; i++) {
                const v = varianceSource[i];
                if (v < vMin) vMin = v;
                if (v > vMax) vMax = v;
            }
            const range = Math.max(0, vMax - vMin);
            hysteresisBand = range * this.revealHysteresis;
        }

        // Keep state per cell to prevent rapid toggles near the cutoff.
        if (!this._revealMaskState || this._revealMaskState.length !== totalCells) {
            this._revealMaskState = new Uint8Array(totalCells);
        }

        // Smooth final alpha mask for video/webcam.
        // If the buffer is (re)created, initialize it to the target alpha so UI changes are instant.
        let alphaMaskJustInitialized = false;
        if (this.isVideo) {
            if (!this._alphaMaskSmoothed || this._alphaMaskSmoothed.length !== totalCells) {
                this._alphaMaskSmoothed = new Float32Array(totalCells);
                alphaMaskJustInitialized = true;
            }
        } else {
            this._alphaMaskSmoothed = null;
        }
        
        for (let i = 0; i < this.gridData.length; i++) {
            const color = this.gridData[i].color;
            const variance = varianceSource ? varianceSource[i] : 9999;

            let shouldReveal;

            // Reveal if variance is within the selected percentile band
            if (this.revealDetails) {
                // Base condition: reveal higher-variance (detailed) regions
                shouldReveal = variance >= thresholdUsed;
                if (hysteresisBand > 0) {
                    const prev = this._revealMaskState[i] === 1;
                    // Hysteresis only helps *stay* revealed; don't make entering harder (avoids dead-zones).
                    if (prev) {
                        shouldReveal = variance >= (thresholdUsed - hysteresisBand);
                    }
                }
            } else {
                // Base condition: reveal lower-variance (flat) regions
                shouldReveal = variance <= thresholdUsed;
                if (hysteresisBand > 0) {
                    const prev = this._revealMaskState[i] === 1;
                    // Hysteresis only helps *stay* revealed; don't make entering harder (avoids dead-zones).
                    if (prev) {
                        shouldReveal = variance <= (thresholdUsed + hysteresisBand);
                    }
                }
            }

            this._revealMaskState[i] = shouldReveal ? 1 : 0;
            
            let alphaTarget = shouldReveal ? 0 : 255;
            if (this.invertReveal) alphaTarget = 255 - alphaTarget;

            let alpha = alphaTarget;
            if (this._alphaMaskSmoothed) {
                const a = this.revealAlphaLerp;
                const smoothed = this._alphaMaskSmoothed;
                if (alphaMaskJustInitialized) {
                    smoothed[i] = alphaTarget;
                    alpha = alphaTarget;
                } else {
                    smoothed[i] = smoothed[i] + (alphaTarget - smoothed[i]) * a;
                    alpha = Math.max(0, Math.min(255, Math.round(smoothed[i])));
                }
            }
            
            alphaData[i * 4] = alpha;
            alphaData[i * 4 + 1] = alpha;
            alphaData[i * 4 + 2] = alpha;
            alphaData[i * 4 + 3] = 255;

            cellMapData[i * 4] = color.r;
            cellMapData[i * 4 + 1] = color.g;
            cellMapData[i * 4 + 2] = color.b;
            cellMapData[i * 4 + 3] = alpha;
        }
        
        gl.bindTexture(gl.TEXTURE_2D, this.alphaMaskTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.gridCols, this.gridRows, 0, gl.RGBA, gl.UNSIGNED_BYTE, alphaData);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);

        gl.bindTexture(gl.TEXTURE_2D, this.cellMapTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, this.gridCols, this.gridRows, 0, gl.RGBA, gl.UNSIGNED_BYTE, cellMapData);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    }
    
    render() {
        if (!this.image && !this.video) return;
        
        const gl = this.gl;
        
        gl.clearColor(0.004, 0.0, 0.012, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        
        gl.useProgram(this.program);
        
        gl.uniform2f(this.resolutionLocation, this.canvas.width, this.canvas.height);

        const src = this.image || this.video;
        const srcW = this.image ? (this.image.width || 1) : (this.video?.videoWidth || 1);
        const srcH = this.image ? (this.image.height || 1) : (this.video?.videoHeight || 1);
        if (this.imageResolutionLocation) {
            gl.uniform2f(this.imageResolutionLocation, srcW, srcH);
        }

        gl.uniform1f(this.gridColsLocation, this.gridCols);
        gl.uniform1f(this.gridRowsLocation, this.gridRows);
        gl.uniform1f(this.gapLocation, this.gap);
        gl.uniform1f(this.cornerRadiusLocation, this.cornerRadius);
        gl.uniform1f(this.hideSourceLocation, this.hideSource);
        if (this.sourceLightenLocation) {
            gl.uniform1f(this.sourceLightenLocation, this.sourceLighten);
        }
        if (this.sourceGrayscaleLocation) {
            gl.uniform1f(this.sourceGrayscaleLocation, this.sourceGrayscale);
        }
        gl.uniform3f(this.bgColorLocation, this.backgroundColor.r, this.backgroundColor.g, this.backgroundColor.b);
        if (this.usePackedCellMapLocation) {
            gl.uniform1f(this.usePackedCellMapLocation, 1.0);
        }
        gl.uniform1f(this.useColorMapLocation, 1.0);
        gl.uniform1f(this.gradientModeLocation, this.gradientMode);
        gl.uniform1f(this.lineModeLocation, this.lineMode);
        if (this.ditherModeLocation) {
            gl.uniform1f(this.ditherModeLocation, this.ditherMode);
        }
        if (this.dominantColorLocation) {
            const dc = this.dominantGridColor || { r: 0, g: 0, b: 0 };
            gl.uniform3f(this.dominantColorLocation, dc.r, dc.g, dc.b);
        }
        if (this.secondaryColorLocation) {
            const sc = this.secondaryGridColor || this.dominantGridColor || { r: 0, g: 0, b: 0 };
            gl.uniform3f(this.secondaryColorLocation, sc.r, sc.g, sc.b);
        }
        
        gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
        gl.enableVertexAttribArray(this.positionLocation);
        gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);
        
        gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
        gl.enableVertexAttribArray(this.texCoordLocation);
        gl.vertexAttribPointer(this.texCoordLocation, 2, gl.FLOAT, false, 0, 0);
        
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.uniform1i(this.imageLocation, 0);
        
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.colorMapTexture);
        gl.uniform1i(this.colorMapLocation, 1);
        
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, this.alphaMaskTexture);
        gl.uniform1i(this.alphaMaskLocation, 2);

        if (this.cellMapLocation) {
            gl.activeTexture(gl.TEXTURE3);
            gl.bindTexture(gl.TEXTURE_2D, this.cellMapTexture);
            gl.uniform1i(this.cellMapLocation, 3);
        }
        
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    resizeToContainer() {
        if (!this.gl) return;
        const source = this.image || this.video;
        if (!source) return;

        const sourceWidth = this.image ? this.image.width : this.video.videoWidth;
        const sourceHeight = this.image ? this.image.height : this.video.videoHeight;
        if (!sourceWidth || !sourceHeight) return;

        const maxWidth = (this.canvas.parentElement?.clientWidth || this.canvas.clientWidth || sourceWidth) - 32;
        const width = Math.max(1, Math.floor(Math.min(sourceWidth, maxWidth)));
        const height = Math.max(1, Math.floor(width * (sourceHeight / sourceWidth)));

        if (this.canvas.width === width && this.canvas.height === height) return;

        this.canvas.width = width;
        this.canvas.height = height;
        this.gl.viewport(0, 0, width, height);

        // Grid cell bounds depend on canvas size.
        if (this.image) {
            this.applyGrouping();
        }
        this.render();
    }

    pauseVideo() {
        if (!this.isVideo || !this.video) return;
        this.isPaused = true;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        this.video.pause();
    }

    async resumeVideo() {
        if (!this.isVideo || !this.video) return;
        this.isPaused = false;
        try {
            await this.video.play();
        } catch {
            // Ignore; user gesture may be required.
        }
        if (!this.animationId) {
            this.startVideoLoop();
        }
    }

    toggleVideoPause() {
        if (!this.isVideo || !this.video) return false;
        if (this.isPaused) {
            // Fire and forget; UI updates immediately
            this.resumeVideo();
            return false;
        }
        this.pauseVideo();
        return true;
    }
    
    rgbToHex(r, g, b) {
        return '#' + [r, g, b].map(x => {
            const hex = Math.round(x).toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        }).join('').toUpperCase();
    }
    
    setGridSize(cols, rows) {
        this.gridCols = cols;
        this.gridRows = rows;
        this.resetRevealOutputSmoothing();
        if (this.image) {
            this.extractRawColors();
            this.applyGrouping();
            this.render();
        }
        // Video will update on next frame automatically, or immediately if paused
        this.updateFromCurrentFrameIfPaused();
    }
    
    setGap(gap) {
        this.gap = gap;
        if (this.image || this.video) {
            this.render();
        }
    }

    setCornerRadius(radius) {
        // Slider is 0..50% mapped to 0..0.5 of the cell
        this.cornerRadius = Math.max(0, Math.min(0.5, radius));
        if (this.image || this.video) {
            this.render();
        }
    }

    setHideSource(enabled) {
        this.hideSource = enabled ? 1.0 : 0.0;
        if (this.image || this.video) {
            this.render();
        }
    }

    setGroupRectMode(enabled) {
        this.groupRectMode = !!enabled;
        if (this.image) {
            this.applyGrouping();
            this.render();
            return;
        }
        // Video will update next frame automatically, or immediately if paused.
        this.updateFromCurrentFrameIfPaused();
    }

    setBackgroundColorRGB(r, g, b) {
        this.backgroundColor.r = Math.max(0, Math.min(1, r));
        this.backgroundColor.g = Math.max(0, Math.min(1, g));
        this.backgroundColor.b = Math.max(0, Math.min(1, b));

        // Avoid extra renders during live video; the next frame will pick it up.
        const isLiveVideo = this.isVideo && this.video && !this.isPaused;
        if (!isLiveVideo && (this.image || this.video)) {
            this.render();
        }
    }

    setSourceLighten(amount) {
        const v = Math.max(-1, Math.min(1, Number(amount) || 0));
        if (this.sourceLighten === v) return;
        this.sourceLighten = v;

        // Avoid extra renders during live video; the next frame will pick it up.
        const isLiveVideo = this.isVideo && this.video && !this.isPaused;
        if (!isLiveVideo && (this.image || this.video)) {
            this.render();
        }
        this.updateFromCurrentFrameIfPaused();
    }

    setSourceGrayscale(enabled) {
        const v = enabled ? 1 : 0;
        if (this.sourceGrayscale === v) return;
        this.sourceGrayscale = v;

        // Avoid extra renders during live video; the next frame will pick it up.
        const isLiveVideo = this.isVideo && this.video && !this.isPaused;
        if (!isLiveVideo && (this.image || this.video)) {
            this.render();
        }
        this.updateFromCurrentFrameIfPaused();
    }
    
    setTargetColors(count) {
        this.targetColors = count;
        this.prevCentroids = null;
        this.resetRevealOutputSmoothing();
        if (this.image) {
            this.applyGrouping();
            this.render();
        }
        this.updateFromCurrentFrameIfPaused();
    }

    setExtractionMode(mode) {
        const normalized = (mode === 'rgb' || mode === 'rgb-kmeans' || mode === 'gifenc') ? mode : 'lab';
        if (this.extractionMode === normalized) return;
        this.extractionMode = normalized;
        this.prevCentroids = null;
        this.resetRevealOutputSmoothing();
        if (this.image) {
            this.applyGrouping();
            this.render();
        }
        // Video will update on next frame automatically, or immediately if paused.
        this.updateFromCurrentFrameIfPaused();
    }

    setGroupThreshold(threshold) {
        this.groupThreshold = threshold;
        this.resetRevealOutputSmoothing();
        if (this.image) {
            this.applyGrouping();
            this.render();
        }
        this.updateFromCurrentFrameIfPaused();
    }
    
    setRevealSize(size) {
        this.revealSize = size;
        this.resetRevealOutputSmoothing();
        if (this.image || this.video) {
            this.updateAlphaMaskTexture();
            this.render();
        }
    }

    setRevealDetails(enabled) {
        this.revealDetails = !!enabled;
        this.resetRevealOutputSmoothing();
        if (this.image || this.video) {
            this.updateAlphaMaskTexture();
            this.render();
        }
    }

    setInvertReveal(enabled) {
        this.invertReveal = !!enabled;
        this.resetRevealOutputSmoothing();
        if (this.image || this.video) {
            this.updateAlphaMaskTexture();
            this.render();
        }
    }

    setGradientMode(enabled) {
        this.gradientMode = enabled ? 1.0 : 0.0;
        if (this.image || this.video) {
            this.render();
        }
    }

    setLineMode(enabled) {
        this.lineMode = enabled ? 1.0 : 0.0;
        if (this.image || this.video) {
            this.render();
        }
    }

    setDitherMode(enabled) {
        this.ditherMode = enabled ? 1.0 : 0.0;
        if (this.image || this.video) {
            this.render();
        }
    }
    
    getGridData() {
        return this.gridData;
    }
    
    exportPNG() {
        // WebGL canvases can export blank images if the backbuffer isn't preserved.
        // Read pixels from the current framebuffer and encode via a 2D canvas.
        if (!this.gl) {
            return this.canvas.toDataURL('image/png');
        }

        // Ensure the latest frame is rendered before capture.
        this.render();

        const gl = this.gl;
        const width = this.canvas.width | 0;
        const height = this.canvas.height | 0;
        if (width <= 0 || height <= 0) {
            return this.canvas.toDataURL('image/png');
        }

        const pixels = new Uint8Array(width * height * 4);
        gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

        const outCanvas = document.createElement('canvas');
        outCanvas.width = width;
        outCanvas.height = height;
        const ctx = outCanvas.getContext('2d');

        const imageData = ctx.createImageData(width, height);
        const rowSize = width * 4;
        // Flip Y (WebGL origin is bottom-left; Canvas is top-left)
        for (let y = 0; y < height; y++) {
            const srcStart = (height - 1 - y) * rowSize;
            const dstStart = y * rowSize;
            imageData.data.set(pixels.subarray(srcStart, srcStart + rowSize), dstStart);
        }
        ctx.putImageData(imageData, 0, 0);

        return outCanvas.toDataURL('image/png');
    }
}
